import { useState } from "react";
import { FormikProps } from "formik";

import { MultipleSelectFilterWithSearch } from "@/components/Common/MultipleSelectFilterWithSearch/MultipleSelectFilterWithSearch";
import { CalendarIcon } from "@/components/Icons/CalendarIcon";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";

import { CONNECTIVITY_TIER_OPTIONS, INVENTORY_REGIONS } from "@/constants";
import { RequestManagedClusterFormType } from "@/types";
import { pluralize } from "@/utils/string";
import { formatDate } from "@/utils/date";
import { cn } from "@/lib/utils";

type RequestManagedClusterFormProps = FormikProps<RequestManagedClusterFormType> & {
  additionalFilters: Record<string, unknown> | undefined;
  onClose: (isOpen: boolean) => void;
  hideConnectivityTier?: boolean;
};

export const RequestManagedClusterForm: React.FC<RequestManagedClusterFormProps> = ({
  values,
  setFieldValue,
  isSubmitting,
  handleSubmit,
  isValid,
  handleBlur,
  handleChange,
  additionalFilters,
  onClose,
  hideConnectivityTier = false
}) => {
  const handleSelection = (name: string, selectedValue: string | number) => {
    const fieldName = name as keyof RequestManagedClusterFormType;
    const currentValues = (values[fieldName] as (string | number)[]) || [];
    const isSelected = currentValues.includes(selectedValue);
    const updatedSelections = isSelected
      ? currentValues.filter((item) => item !== selectedValue)
      : [...currentValues, selectedValue];

    setFieldValue(fieldName, updatedSelections);
  };
  const [calendarOpen, setCalendarOpen] = useState(false);

  return (
    <form onSubmit={handleSubmit}>
      <div className="custom-scrollbar flex max-h-[500px] flex-col gap-1 overflow-y-auto overflow-x-hidden lg:max-h-[620px]">
        <MultipleSelectFilterWithSearch
          title={`Region * ${values.regions.length ? `(${pluralize("value", values.regions.length)} selected)` : ""}`}
          name="regions"
          options={INVENTORY_REGIONS}
          selectedOptions={values.regions}
          onSelect={(fieldName, selectedValue) => {
            handleSelection(fieldName, selectedValue);
          }}
          includeSearch={false}
        />
        <Separator />
        {!hideConnectivityTier && (
          <MultipleSelectFilterWithSearch
            title={`Connectivity Tier * ${values.connectivityTiers.length ? `(${pluralize("value", values.connectivityTiers.length)} selected)` : ""}`}
            name="connectivityTiers"
            options={CONNECTIVITY_TIER_OPTIONS}
            selectedOptions={values.connectivityTiers}
            onSelect={(fieldName, selectedValue) => {
              handleSelection(fieldName, selectedValue);
            }}
            includeSearch={false}
          />
        )}
        {!hideConnectivityTier && <Separator />}
        {additionalFilters?.hardware_names &&
        (additionalFilters?.hardware_names as string[])?.length > 0 ? (
          <MultipleSelectFilterWithSearch
            title={`Hardware * ${values.hardwareNames.length ? `(${pluralize("value", values.hardwareNames.length)} selected)` : ""}`}
            name="hardwareNames"
            options={additionalFilters?.hardware_names as string[]}
            selectedOptions={values.hardwareNames}
            onSelect={(fieldName, selectedValue) => {
              handleSelection(fieldName, selectedValue);
            }}
          />
        ) : (
          <div className="flex flex-col gap-2 py-2">
            <label className="label-small text-gray-dark-1000">Hardware *</label>
            <Input
              name="hardwareNames"
              value={values.hardwareNames.join(", ")}
              disabledOnWheel={true}
              onBlur={handleBlur}
              onChange={(e) => setFieldValue("hardwareNames", e.target.value.split(", "))}
              className="placeholder:subtext-x-small ring-0 focus:border-gray-dark-200 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 "
            />
          </div>
        )}
        <Separator />
        <div className="flex flex-col gap-2 py-2">
          <label className="label-small text-gray-dark-1000">Number of GPUs Needed *</label>
          <Input
            type="number"
            name="gpuQuantity"
            value={values.gpuQuantity}
            disabledOnWheel={true}
            onBlur={handleBlur}
            onChange={(e) => {
              if (parseInt(e.target.value) < 1) {
                setFieldValue("gpuQuantity", "1");
              } else {
                handleChange(e);
              }
            }}
            className="placeholder:subtext-x-small ring-0 focus:border-gray-dark-200 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 "
          />
        </div>
        <div className="flex flex-row items-center gap-2 py-2">
          <Checkbox
            id="confidentialCompute"
            checked={values.confidentialCompute}
            onCheckedChange={(checked) => setFieldValue("confidentialCompute", !!checked)}
          />
          <label className="label-small text-gray-dark-1000">Confidential Compute</label>
        </div>
        <div className="flex flex-col gap-2 py-2">
          <label className="label-small text-gray-dark-1000">Contact Email *</label>
          <Input
            name="contactEmail"
            value={values.contactEmail}
            onBlur={handleBlur}
            onChange={handleChange}
            className="placeholder:subtext-x-small ring-0 focus:border-gray-dark-200 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 "
          />
        </div>
        <div className="flex flex-col gap-2 py-2">
          <label className="label-small text-gray-dark-1000">Telegram Handle</label>
          <Input
            name="telegramHandle"
            value={values.telegramHandle}
            onBlur={handleBlur}
            onChange={handleChange}
            className="placeholder:subtext-x-small ring-0 focus:border-gray-dark-200 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 "
          />
        </div>
        <div className="flex flex-col gap-2 py-2">
          <label className="label-small text-gray-dark-1000">Required By</label>
          <Popover modal={true} open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start gap-2 text-left font-normal",
                  !values.neededBy && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="h-4 w-4" />
                {values.neededBy ? (
                  formatDate(values.neededBy.toISOString())
                ) : (
                  <span>Pick a date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="!z-50 w-auto !p-0" align="start">
              <Calendar
                mode="single"
                selected={values.neededBy}
                onSelect={(value) => {
                  setFieldValue("neededBy", value);
                  setCalendarOpen(false);
                }}
                disabled={{ before: new Date() }}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setCalendarOpen(false);
              setTimeout(() => {
                onClose(false);
              }, 50);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={!isValid || isSubmitting} variant="primary">
            Request
          </Button>
        </div>
      </div>
    </form>
  );
};
